// The SEALED credential that crosses the wire, how it is built, and how it is
// projected for audit — plus the tier-routing provenance it stamps. See
// `../llm-credential.ts` for the file docblock.

import { z } from 'zod';
import { type BYOKKey, BYOKTierSchema } from '../../entities/byok';
import { detectedReasoning, ReasoningCapabilitySchema } from '../reasoning-capability';
import { LlmAuthStyleSchema, LlmWireSchema, PROVIDER_WIRE, wireFor } from './wire';

// ─── tier-routing provenance (#510) ──────────────────────────────────────────

/**
 * Which precedence level resolved the tier/model shipped on a sealed credential
 * (#510, army-orchestration §1.4). Stamped for audit — "who decided this run's
 * model." Precedence, highest wins:
 *
 * - `preference` — an explicit envelope `TaskLlmPreference` (exact provider/model/key).
 * - `route`      — a purpose route pin (`llm_routes`, arc 101 slice 03): repo route
 *   over org route, keyed on the task's purpose (job class / review / gardener).
 * - `donation`   — the RECIPIENT's pick on a donated key (`donations.chosen_model`,
 *   arc 101 slice 03). Shares the `user_model` rung — below an envelope/route pin,
 *   above the key's own `model` — but outranks it when both apply: the donor's
 *   `allowed_models` ceiling may not authorize the member's stored default, and
 *   `resolveDonatedModel` refuses rather than downgrades.
 * - `user_model` — the dispatching member's stored model default (epic #635) picked the model.
 * - `policy`     — a repo `.maintainer.yml` `coding.models` per-job-class tier.
 * - `job_class`  — the job-class default map (`JOB_CLASS_ROUTES`).
 * - `default`    — the account/general fallback tier (nothing more specific matched).
 */
export const LlmTierSource = {
  Preference: 'preference',
  Route: 'route',
  Donation: 'donation',
  UserModel: 'user_model',
  Policy: 'policy',
  JobClass: 'job_class',
  Default: 'default',
  /**
   * #2203: this pick was NOT decided by the tier ladder at all — it is the
   * SIBLING-PROVIDER key sealed beside the primary for the review ensemble's
   * top-up, chosen because it sits on a different endpoint than the primary.
   * Stamping it with the primary's own routing source would claim the ladder
   * decided a pick it never saw.
   */
  Fallback: 'fallback',
} as const;
export const LlmTierSourceSchema = z.enum(LlmTierSource);
export type LlmTierSource = z.infer<typeof LlmTierSourceSchema>;

// ─── the sealed credential (wire) ────────────────────────────────────────────

/**
 * The resolved LLM credential the assigner ships in `FleetTaskAssignment`.
 * snake_case: it crosses NATS verbatim, same convention as `TaskEnvelope`.
 *
 * `sealed_key` is base64 of `byok_keys.encrypted_key` re-wrapped for the CLAIMING
 * box's subkey (#1300) — NOT plaintext, and not byte-equal to the stored column.
 * It is decrypted only on that runner box, only into the child's spawn env; a leak
 * into a log/event is scrubbed by `@developerz/audit`. Never serialize this object
 * directly — audit it through {@link llmCredentialAuditFields}.
 */
export const SealedLlmCredentialSchema = z.object({
  /** The `byok_keys` row id — for reassignment bookkeeping + audit metadata. */
  key_id: z.string().min(1),
  /** Account-unique handle, echoed for audit/event lines; null when unnamed. */
  key_label: z.string().min(1).max(64).nullable(),
  provider: z.string().min(1),
  model: z.string().min(1).max(200),
  /** The endpoint the box will point at; null = Anthropic direct, pinned to `ANTHROPIC_DIRECT_ORIGIN`. */
  base_url: z.string().min(1).nullable(),
  wire: LlmWireSchema,
  auth_style: LlmAuthStyleSchema,
  /**
   * The capability tier this run was routed to (#510) — audit context, echoed on
   * the assignment/run event. Absent = untier-routed (a legacy assignment / a pool
   * with no tier signal); the box never reads it, only logs it.
   */
  tier: BYOKTierSchema.optional(),
  /** Which precedence level resolved the tier/model (#510, audit). Absent = untier-routed. */
  resolved_by: LlmTierSourceSchema.optional(),
  /** A dropped envelope model pin; provenance still names the actual deciding rung. */
  envelope_model_drop_reason: z.literal('envelope_model_unserved').optional(),
  /**
   * This run was sealed OFF its lane's Agents-page selection: the selection names no key
   * that would serve the lane (revoked, deleted, fenced to another lane), so the claim
   * ran the lane's own order and `resolved_by` reads as an Automatic claim's would. This
   * field is what tells the two apart on the credential; the durable row is
   * `dispatch.no_credential` with `kind: route_pin_unserved` (`apps/worker`
   * `dispatch/no-credential.ts`).
   *
   * A separate optional field, NOT a new `resolved_by` member: `LlmTierSourceSchema` is
   * a closed enum this object is parsed with on every box, so a member an older runner
   * does not know would make the whole assignment undecodable there for as long as that
   * runner is deployed. An unknown KEY is stripped (this object is not `.strict()`).
   */
  selection_unserved: z.literal(true).optional(),
  /**
   * The GIFT this key came from (`donations.id`, arc 101 slice 12 / plan doc 07) —
   * present only when the sealed key is DONATED (earmarked, with a live donation
   * row behind it), absent for an account's own key.
   *
   * The donor pays for every token this credential signs, on a machine and a repo
   * they do not own, so "a donor can be shown exactly what their key ran" needs the
   * gift id on the run itself: `resolved_by: 'donation'` says a gift decided the
   * model, `key_id` says which key, and only this says WHICH GIFT — the one thing
   * that ties a run back to the `donation.created` row and its `allowed_models`
   * ceiling. A row id, never a credential; the box only logs it.
   */
  donation_id: z.string().min(1).optional(),
  /**
   * The DETECTED reasoning capability of THIS (key, model) — `byok_key_models.reasoning`,
   * {@link ReasoningCapabilitySchema}. The box maps it to the platform default (HIGH where
   * supported; `planAssignmentReasoning`, `apps/runner`). On the credential, not beside it,
   * because it is a fact about exactly this pair: a failover key or a review sibling carries
   * its own, and no mismatch is possible. Absent = never checked, and the box sends no
   * reasoning parameter; an old box strips the key (this object is not `.strict()`).
   */
  reasoning: ReasoningCapabilitySchema.optional(),
  /** base64(KEK-wrapped ciphertext). Unwrapped ONLY on the box, with the box KEK. */
  sealed_key: z.string().min(1),
});
export type SealedLlmCredential = z.infer<typeof SealedLlmCredentialSchema>;

/**
 * The non-secret, wire-policy fields of a sealed credential, derived purely from
 * a resolved pool key. The caller (the assigner) adds `sealed_key` — base64 of
 * `key.encryptedKey` — to complete the {@link SealedLlmCredential}. Kept here so
 * the provider→wire policy lives in ONE place (domain), not split across the
 * worker (seal) and runner (consume).
 */
export function sealedCredentialFields(
  key: Pick<BYOKKey, 'id' | 'label' | 'provider' | 'model' | 'baseUrl' | 'models'>,
  /**
   * Optional per-user MODEL override (the per-user provider cascade, epic #635) —
   * "use the org's provider+key, but a DIFFERENT model". When set it REPLACES the
   * key row's own `model` on the wire; the sealed bytes, provider, and endpoint
   * are untouched, so the account keeps signing with its key but talks to the
   * model the member chose. The caller must only pass a model the winning key's
   * provider actually serves (the resolver already ranks a same-provider key
   * first), so this is a pure field swap — no wire-policy change. Absent → the
   * key's own model, the pre-cascade behaviour.
   *
   * A DONATED key's resolved model (arc 101 slice 03) rides this same rung —
   * {@link ResolveLlmResult.donatedModels}`[key.id]`, already checked against the
   * donor's ceiling by {@link resolveLlmFromPool}. So does a TAGGED model (arc 101
   * slice 02): {@link ResolvedLlmPick.model}, the model the pool resolver chose out
   * of the key's `byok_key_models` catalogue for this task's tier. Same mechanic,
   * three sources, one channel: this function stays the ONE place a model swap
   * happens on the wire, which is why the slice extends the RESOLUTION to produce
   * the override rather than adding a second model field to the sealed shape.
   */
  modelOverride?: string | null,
): Omit<SealedLlmCredential, 'sealed_key'> {
  const policy = PROVIDER_WIRE[key.provider];
  // The wire follows a recognized endpoint, not only the label (K8) — `wireFor`
  // is the one seam; never read `policy.wire` here directly.
  const { wire, authStyle } = wireFor(key.provider, key.baseUrl ?? null);
  const model = modelOverride?.trim() ? modelOverride.trim() : key.model;
  // The catalogue's verdict for the model ACTUALLY sealed (after any override).
  const reasoning = detectedReasoning(key, model);
  return {
    key_id: key.id,
    key_label: key.label ?? null,
    provider: key.provider,
    model,
    base_url: key.baseUrl ?? policy.defaultBaseUrl,
    wire,
    auth_style: authStyle,
    ...(reasoning === null ? {} : { reasoning }),
  };
}

/**
 * The one structured audit rendering of a dispatched credential (plan 102 slice 01)
 * — which key served the run, on which model, and which rung decided it. The
 * `task.assigned` audit row carries this as `output.llm`, so "which key served this
 * run" and "did the model ladder guess" are one GROUP BY over `audit_events`, with no
 * outbox read.
 *
 * An ALLOWLIST, and it must stay one: every field is copied by name, so `sealed_key`,
 * `base_url` (which can embed userinfo auth) and `auth_style` cannot reach a row
 * however the sealed shape grows. `.strict()` makes the schema refuse an extra key,
 * and {@link llmCredentialAuditFields} parses its own result through it, so a spread
 * of the credential into the row throws at that seam instead of landing.
 *
 * Every field is REQUIRED and nullable — never optional. An untier-routed credential
 * stamps `tier: null`, not an absent key, so a reader never has to tell "absent" from
 * "null" apart. `donation_id` is the gift that paid (arc 101 slice 12): the only field
 * that ties a donated run back to its `donation.created` row.
 *
 * The object itself is nullable: `null` is the row for a dispatch that shipped NO
 * sealed credential (box env, or no usable pool — the `dispatch.no_credential` row
 * names why).
 */
export const DispatchLlmAuditSchema = z
  .object({
    key_id: SealedLlmCredentialSchema.shape.key_id,
    provider: SealedLlmCredentialSchema.shape.provider,
    model: SealedLlmCredentialSchema.shape.model,
    resolved_by: LlmTierSourceSchema.nullable(),
    envelope_model_drop_reason: z.literal('envelope_model_unserved').nullable(),
    tier: BYOKTierSchema.nullable(),
    /** The sibling-provider key sealed beside the primary (#2203), or null. */
    fallback_key_id: SealedLlmCredentialSchema.shape.key_id.nullable(),
    donation_id: z.string().min(1).nullable(),
  })
  .strict()
  .nullable();
export type DispatchLlmAudit = z.infer<typeof DispatchLlmAuditSchema>;

/**
 * Project the assignment's sealed credential (and its #2203 fallback) into
 * {@link DispatchLlmAuditSchema}. No primary → `null`: a fallback with no primary
 * shipped nothing the run can be attributed to.
 */
export function llmCredentialAuditFields(
  primary: SealedLlmCredential | null | undefined,
  fallback?: SealedLlmCredential | null,
): DispatchLlmAudit {
  if (!primary) return null;
  // Parsed, not merely typed: the schema's `.strict()` is enforced HERE, at the one
  // seam every writer goes through, so the allowlist claim above is a runtime fact.
  return DispatchLlmAuditSchema.parse({
    key_id: primary.key_id,
    provider: primary.provider,
    model: primary.model,
    resolved_by: primary.resolved_by ?? null,
    envelope_model_drop_reason: primary.envelope_model_drop_reason ?? null,
    tier: primary.tier ?? null,
    fallback_key_id: fallback?.key_id ?? null,
    donation_id: primary.donation_id ?? null,
  });
}
